import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { BillingModule, PaymentStatus, Plan, PlanSelection, Subscription } from '../app/models/subscription';

/** The price list labs buy from, and what each of them bought. Prices are in EUR. */
@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private readonly url = `${environment.api.baseUrl}/v1/admin`;

  constructor(private http: HttpClient) {}

  getPlans(): Observable<{ items: Plan[] }> {
    return this.http.get<{ items: Plan[] }>(`${this.url}/plans`);
  }

  updatePlan(plan: Plan): Observable<Plan> {
    return this.http.put<Plan>(`${this.url}/plans/${plan.code}`, plan);
  }

  getModules(): Observable<{ items: BillingModule[] }> {
    return this.http.get<{ items: BillingModule[] }>(`${this.url}/modules`);
  }

  updateModule(module: BillingModule): Observable<BillingModule> {
    return this.http.put<BillingModule>(`${this.url}/modules/${module.code}`, module);
  }

  getSubscriptions(paymentStatus: PaymentStatus | null): Observable<{ items: Subscription[] }> {
    const params = paymentStatus ? new HttpParams().set('paymentStatus', paymentStatus) : undefined;
    return this.http.get<{ items: Subscription[] }>(`${this.url}/subscriptions`, { params });
  }

  /** Empty body (204) when the tenant is on the demo period. */
  getTenantSubscription(tenantId: number): Observable<Subscription | null> {
    return this.http.get<Subscription | null>(`${this.url}/tenants/${tenantId}/subscription`);
  }

  markPaid(id: number): Observable<Subscription> {
    return this.http.post<Subscription>(`${this.url}/subscriptions/${id}/mark-paid`, null);
  }

  markUnpaid(id: number): Observable<Subscription> {
    return this.http.post<Subscription>(`${this.url}/subscriptions/${id}/mark-unpaid`, null);
  }

  approve(id: number): Observable<Subscription> {
    return this.http.post<Subscription>(`${this.url}/subscriptions/${id}/approve`, null);
  }

  reject(id: number): Observable<Subscription> {
    return this.http.post<Subscription>(`${this.url}/subscriptions/${id}/reject`, null);
  }

  change(tenantId: number, selection: PlanSelection): Observable<Subscription> {
    return this.http.put<Subscription>(`${this.url}/tenants/${tenantId}/subscription`, selection);
  }

  renew(id: number): Observable<Subscription> {
    return this.http.post<Subscription>(`${this.url}/subscriptions/${id}/renew`, null);
  }
}
